// client/src/coms/components/PackMarketplaceDialogs.js
// The creator's Marketplace actions for a server-hosted pack: publish (free or with a price), change the
// price, see basic statistics, and copy the pack to this device as a local pack. The server validates
// every price (integer minor units, allowed range) and decides what is shown to buyers.
import React, { useEffect, useState } from 'react';
import { comsApiGet, comsApiPost, comsApiPut } from '../comsApi';
import { ModalShell } from './StickerEditorModal';
import { formatPrice } from './PackInfoDialog';
import { useStickerHosting } from './HostingSelector';
import { addLocalSticker, createLocalPack } from '../stickers/localStickerStore';

const field = { background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: '8px 10px', fontSize: 13 };

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

// "1.99" -> 199; invalid -> NaN. Whole minor units only.
export function parsePriceInput(text) {
  const t = String(text || '').trim().replace(/^[£$€]/, '');
  if (!/^\d{1,4}(\.\d{1,2})?$/.test(t)) return NaN;
  const [whole, frac = ''] = t.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

// mode: 'publish' (list it, choosing the price) or 'price' (change the price of a listed pack).
export function PublishDialog({ pack, mode = 'publish', onClose, onDone }) {
  const hosting = useStickerHosting();
  const market = (hosting && hosting.marketplace) || { currency: 'GBP', min_paid_minor: 49, max_minor: 4999, price_points_minor: null };
  const currentMinor = pack.price ? pack.price.amount_minor : 0;
  const [paid, setPaid] = useState(currentMinor > 0);
  const [amount, setAmount] = useState(currentMinor > 0 ? (currentMinor / 100).toFixed(2) : '0.99');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const minor = paid ? parsePriceInput(amount) : 0;
  const valid = !paid || (Number.isFinite(minor) && minor >= market.min_paid_minor && minor <= market.max_minor);
  const money = (m) => formatPrice({ amount_minor: m, currency: market.currency });

  async function submit() {
    setBusy(true);
    setErr('');
    try {
      const body = { amount_minor: minor, currency: market.currency };
      const r = mode === 'publish' ? await comsApiPost(`/stickers/packs/${pack.id}/publish`, body) : await comsApiPut(`/stickers/packs/${pack.id}/price`, body);
      onDone(r.pack);
    } catch (e) {
      setErr(errText(e, mode === 'publish' ? 'Could not publish the pack.' : 'Could not change the price.'));
      setBusy(false);
    }
  }

  return (
    <ModalShell label={mode === 'publish' ? `Publish ${pack.name}` : `Price of ${pack.name}`} onClose={() => !busy && onClose()} width={480}>
      <div style={{ fontSize: 17, fontWeight: 900 }}>{mode === 'publish' ? `Publish “${pack.name}” to the Marketplace` : `Price of “${pack.name}”`}</div>
      {mode === 'publish' ? (
        <div style={{ fontSize: 13, opacity: 0.8, marginTop: 6, lineHeight: 1.45 }}>
          Anyone can find it by name, by your username and by its keywords, preview it, and add it. You can unpublish it at any time; people who already have it keep it.
        </div>
      ) : null}
      <div role="radiogroup" aria-label="Price" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
        <label className={`vox-hosting-option ${!paid ? 'selected' : ''}`}>
          <input type="radio" name="price" checked={!paid} onChange={() => setPaid(false)} />
          <span><span className="vox-hosting-title">Free</span><span className="vox-hosting-line">Anyone can add it straight away</span></span>
        </label>
        <label className={`vox-hosting-option ${paid ? 'selected' : ''}`}>
          <input type="radio" name="price" checked={paid} onChange={() => setPaid(true)} />
          <span style={{ flex: 1 }}>
            <span className="vox-hosting-title">Paid</span>
            <span className="vox-hosting-line">Between {money(market.min_paid_minor)} and {money(market.max_minor)}. People who already added it for free keep it.</span>
            {paid ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <span aria-hidden="true">{market.currency === 'GBP' ? '£' : market.currency}</span>
                <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" aria-label={`Price in ${market.currency}`} style={{ ...field, width: 110 }} data-testid="pack-price-input" />
              </span>
            ) : null}
          </span>
        </label>
      </div>
      {paid ? (
        <div style={{ fontSize: 12, marginTop: 10, padding: 10, borderRadius: 10, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', lineHeight: 1.45 }}>
          Payments are not active on Voxemera yet. The pack is listed with its price and people can preview it; buying opens once payment processing is activated. Nobody can add a paid pack without buying it.
        </div>
      ) : null}
      {!valid ? <div className="coms-form-error" style={{ marginTop: 8 }}>Enter a price between {money(market.min_paid_minor)} and {money(market.max_minor)}.</div> : null}
      {err ? <div className="coms-form-error" role="alert" style={{ marginTop: 10 }}>{err}</div> : null}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="coms-mini-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="coms-mini-btn primary" onClick={submit} disabled={busy || !valid} data-testid="pack-publish-confirm">
          {busy ? 'Saving…' : mode === 'publish' ? (paid ? `Publish for ${money(minor)}` : 'Publish for free') : 'Save price'}
        </button>
      </div>
    </ModalShell>
  );
}

export function StatsDialog({ pack, onClose }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    comsApiGet(`/stickers/packs/${pack.id}/stats`).then((r) => setStats(r.stats)).catch((e) => setErr(errText(e, 'Could not load statistics.')));
  }, [pack.id]);
  const rows = stats
    ? [
        ['People who added it', stats.installs],
        ['Purchases', stats.purchases],
        ['Shared with accounts', stats.shared_with],
        ['Servers using it', stats.servers],
        ['Stickers', stats.stickers],
        ['Stickers sent at least once', stats.stickers_sent],
        ['Marketplace', stats.marketplace_status === 'published' ? 'Published' : stats.marketplace_status === 'unpublished' ? 'Unpublished' : 'Not listed'],
        ['Revenue', stats.revenue.length ? stats.revenue.map((r) => formatPrice({ amount_minor: r.gross_minor, currency: r.currency })).join(', ') : 'None yet'],
      ]
    : [];
  return (
    <ModalShell label={`Statistics for ${pack.name}`} onClose={onClose} width={420}>
      <div style={{ fontSize: 17, fontWeight: 900 }}>“{pack.name}” statistics</div>
      {err ? <div className="coms-form-error" role="alert" style={{ marginTop: 10 }}>{err}</div> : null}
      {!stats && !err ? <div style={{ marginTop: 12, opacity: 0.7, fontSize: 13 }}>Loading…</div> : null}
      <dl className="vox-pack-stats">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button type="button" className="coms-mini-btn" onClick={onClose}>Close</button>
      </div>
    </ModalShell>
  );
}

// Server -> local: a local copy of a pack you own. The server pack stays as it is (and keeps working for
// everyone who has it); remove it separately if you want.
export async function copyPackToDevice(pack, onProgress = () => {}) {
  const local = await createLocalPack({ name: pack.name, description: pack.description || '' });
  let done = 0;
  for (const s of pack.stickers || []) {
    onProgress(done, (pack.stickers || []).length);
    const res = await fetch(s.url, { credentials: 'include' });
    if (!res.ok) throw new Error(`Could not download “${s.name}”`);
    const blob = await res.blob();
    await addLocalSticker(local.id, { name: s.name, keywords: s.keywords || [], blob, mime: blob.type || 'image/webp', animated: !!s.animated });
    done += 1;
  }
  onProgress(done, done);
  return local;
}
